import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { EmptyState, Eyebrow, Pagination, SegmentedControl } from '@/shared/components';
import { useApi, type ChannexWebhookEventResponse } from '@/shared/api';
import { statusLabel, webhookStatusOptions, type WebhookStatusFilter } from './helpers';
import { WebhookEventTable } from './WebhookEventTable';
import { WebhookEventDetailDialog } from './WebhookEventDetailDialog';

export function WebhookEventLog() {
  const api = useApi();
  const [filter, setFilter] = useState<WebhookStatusFilter>('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ChannexWebhookEventResponse | null>(null);
  const pageSize = 25;

  const { data, isLoading, error } = useQuery({
    queryKey: ['channex', 'webhook-events', { page, pageSize, filter }],
    queryFn: () =>
      api.channex.listWebhookEvents({
        page,
        pageSize,
        ...(filter !== 'all' ? { status: filter } : {}),
      }),
    placeholderData: (prev) => prev,
  });

  return (
    <section className="mt-12">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <Eyebrow variant="muted">Meldungs-Protokoll</Eyebrow>
          <h2 className="text-ink mt-2 font-serif text-2xl">
            Eingehende <span className="font-serif-italic">Meldungen</span>
            <span className="text-sealwax">.</span>
          </h2>
          {data && (
            <p className="text-ink-soft mt-1 text-xs">
              {data.total} {data.total === 1 ? 'Meldung' : 'Meldungen'} insgesamt
            </p>
          )}
        </div>
        <SegmentedControl
          value={filter}
          onChange={(v) => {
            setFilter(v);
            setPage(1);
          }}
          options={webhookStatusOptions}
          className="[&>button]:min-h-11"
        />
      </div>

      {error ? (
        <EmptyState
          icon="Error"
          title="Konnte die Meldungen nicht laden"
          description="Bitte später erneut versuchen — besteht das Problem fort, ist die Anbindung an den Kanalmanager gestört."
        />
      ) : !isLoading && data && data.items.length === 0 ? (
        <EmptyState
          icon="Inbox"
          title="Keine Meldungen"
          description={
            filter === 'all'
              ? 'Sobald der Kanalmanager etwas schickt, steht es hier.'
              : `Keine Meldung im Zustand „${statusLabel(filter)}".`
          }
        />
      ) : (
        <>
          <WebhookEventTable items={data?.items ?? []} onSelect={setSelected} />
          {data && (
            <Pagination page={page} pageSize={pageSize} total={data.total} onPageChange={setPage} />
          )}
        </>
      )}

      <WebhookEventDetailDialog event={selected} onOpenChange={(o) => !o && setSelected(null)} />
    </section>
  );
}
